<template>
    <div class="property-list-card">
        <div class="property-list-card-inner">

            <!-- Property Image -->
            <div class="property-list-image">
                <a :href="productUrl">

                    <template v-if="isMember && hasBaseImage">
                        <img
                            :src="baseImage"
                            alt="Property Image"
                            class="property-list-main-image"
                        >
                    </template>

                    <template v-else>
                        <div class="property-list-locked-image">
                            <div class="property-list-lock-content">
                                <i class="las la-lock"></i>
                                <strong>Premium Property Image</strong>
                                <span>
                                    Membership required to view photos
                                </span>
                            </div>
                        </div>
                    </template>

                </a>

                <!-- Auction Status -->
                <span
                    v-if="product.auction_status"
                    class="property-list-status"
                    :class="statusClass"
                >
                    {{ product.auction_status }}
                </span>
            </div>

            <!-- Property Details -->
            <div class="property-list-content">

                <!-- Property ID -->
                <div
                    class="property-list-id"
                    v-if="product.property_id"
                >
                    Property ID: {{ product.property_id }}
                </div>

                <!-- Property Title -->
                <a
                    :href="productUrl"
                    class="property-list-title"
                >
                    {{ product.name }}
                </a>

                <!-- Property Type -->
                <div
                    class="property-list-type"
                    v-if="product.property_type"
                >
                    {{ product.property_type }}
                </div>

                <!-- Location -->
                <div class="property-list-location">
                    <i class="las la-map-marker"></i>

                    <span>
                        {{ product.city }}

                        <template v-if="product.state">
                            , {{ product.state }}
                        </template>

                        <template v-if="product.locality">
                            <br>
                            {{ product.locality }}
                        </template>
                    </span>
                </div>

                <!-- Auction Authority -->
                <div
                    class="property-list-authority"
                    v-if="product.auction_authority"
                >
                    <span>Auction Authority</span>

                    <strong>
                        {{ product.auction_authority }}
                    </strong>
                </div>

                <!-- Member Auction Details -->
                <div
                    v-if="isMember"
                    class="property-list-member-details"
                >

                    <!-- Auction Date -->
                    <div
                        class="property-list-detail"
                        v-if="product.auction_date"
                    >
                        <span>Auction Date</span>
                        <strong>
                            {{ formattedAuctionDate }}
                        </strong>
                    </div>

                    <!-- Auction Time -->
                    <div
                        class="property-list-detail"
                        v-if="product.auction_time"
                    >
                        <span>Auction Time</span>
                        <strong>
                            {{ product.auction_time }}
                        </strong>
                    </div>

                    <!-- Reserve Price -->
                    <div
                        class="property-list-detail"
                        v-if="product.reserve_price"
                    >
                        <span>Reserve Price</span>
                        <strong>
                            ₹{{ formatAmount(product.reserve_price) }}
                        </strong>
                    </div>

                    <!-- EMD -->
                    <div
                        class="property-list-detail"
                        v-if="product.emd_amount"
                    >
                        <span>EMD Amount</span>
                        <strong>
                            ₹{{ formatAmount(product.emd_amount) }}
                        </strong>
                    </div>

                </div>

                <!-- Non Member Locked Information -->
                <div
                    v-else
                    class="property-list-locked-details"
                >
                    <div class="property-list-lock-icon">
                        <i class="las la-lock"></i>
                    </div>

                    <div>
                        <strong>
                            Premium Property Details
                        </strong>

                        <span>
                            Join membership to view auction date,
                            reserve price, EMD and other premium details.
                        </span>
                    </div>
                </div>

                <!-- View Property -->
                <div class="property-list-action">
                    <a
                        :href="productUrl"
                        class="btn btn-primary property-list-view-button"
                    >
                        <i class="las la-eye"></i>
                        View Property
                    </a>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: ['product'],

    computed: {
        productUrl() {
            return route('products.show', this.product.slug);
        },

        isMember() {
            return window.FleetCart &&
                window.FleetCart.isMember === true;
        },

        hasBaseImage() {
            return (
                this.product.base_image &&
                this.product.base_image.length !== 0
            );
        },

        baseImage() {
            if (this.hasBaseImage) {
                return this.product.base_image.path;
            }

            return `${window.FleetCart.baseUrl}/themes/storefront/public/images/image-placeholder.png`;
        },

        statusClass() {
            if (!this.product.auction_status) {
                return '';
            }

            return this.product.auction_status
                .toLowerCase()
                .replace(/\s+/g, '-');
        },

        formattedAuctionDate() {
            if (!this.product.auction_date) {
                return '';
            }

            const date = new Date(this.product.auction_date);

            if (Number.isNaN(date.getTime())) {
                return this.product.auction_date;
            }

            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        }
    },

    methods: {
        formatAmount(amount) {
            const number = Number(amount);

            if (Number.isNaN(number)) {
                return amount;
            }

            return number.toLocaleString('en-IN', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            });
        }
    }
};
</script>

<style scoped>

.property-list-card {
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 20px;
    transition: all 0.2s ease;
}

.property-list-card:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.property-list-card-inner {
    display: flex;
    width: 100%;
}

.property-list-image {
    position: relative;
    width: 320px;
    min-width: 320px;
    height: 280px;
    overflow: hidden;
    background: #f5f5f5;
}

.property-list-image > a {
    display: block;
    width: 100%;
    height: 100%;
}

.property-list-main-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.property-list-locked-image {
    width: 100%;
    height: 100%;
    min-height: 280px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    background:
        linear-gradient(
            135deg,
            #eeeeee 0%,
            #d9d9d9 50%,
            #eeeeee 100%
        );
}

.property-list-lock-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 25px;
    color: #555;
}

.property-list-lock-content i {
    font-size: 45px;
    margin-bottom: 12px;
}

.property-list-lock-content strong {
    font-size: 15px;
    margin-bottom: 5px;
}

.property-list-lock-content span {
    font-size: 12px;
    line-height: 18px;
}

.property-list-status {
    position: absolute;
    top: 12px;
    left: 12px;
    padding: 6px 12px;
    border-radius: 20px;
    background: #333;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
}

.property-list-status.upcoming {
    background: #1976d2;
}

.property-list-status.live {
    background: #2e7d32;
}

.property-list-status.closed,
.property-list-status.sold {
    background: #757575;
}

.property-list-status.cancelled {
    background: #c62828;
}

.property-list-content {
    flex: 1;
    padding: 22px 25px;
    display: flex;
    flex-direction: column;
}

.property-list-id {
    color: #888;
    font-size: 12px;
    margin-bottom: 5px;
}

.property-list-title {
    color: #222;
    font-size: 21px;
    line-height: 28px;
    font-weight: 600;
    text-decoration: none;
    margin-bottom: 6px;
}

.property-list-title:hover {
    color: #007bff;
}

.property-list-type {
    color: #666;
    font-size: 13px;
    margin-bottom: 12px;
}

.property-list-location {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #555;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 12px;
}

.property-list-location i {
    font-size: 19px;
    margin-top: 1px;
}

.property-list-authority {
    padding: 10px 0;
    border-top: 1px solid #eeeeee;
    border-bottom: 1px solid #eeeeee;
    margin-bottom: 10px;
}

.property-list-authority span {
    display: block;
    color: #888;
    font-size: 11px;
    margin-bottom: 3px;
}

.property-list-authority strong {
    color: #333;
    font-size: 13px;
}

.property-list-member-details {
    display: grid;
    grid-template-columns: repeat(2, minmax(150px, 1fr));
    gap: 6px 25px;
    margin-bottom: 12px;
}

.property-list-detail {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    padding: 5px 0;
    font-size: 12px;
}

.property-list-detail span {
    color: #777;
}

.property-list-detail strong {
    color: #222;
    text-align: right;
}

.property-list-locked-details {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 13px;
    background: #f8f8f8;
    border: 1px solid #eeeeee;
    border-radius: 7px;
    margin-bottom: 12px;
}

.property-list-lock-icon {
    font-size: 27px;
    flex-shrink: 0;
}

.property-list-locked-details strong {
    display: block;
    font-size: 13px;
    margin-bottom: 3px;
}

.property-list-locked-details span {
    display: block;
    color: #777;
    font-size: 11px;
    line-height: 16px;
}

.property-list-action {
    margin-top: auto;
}

.property-list-view-button {
    display: inline-block;
    min-width: 160px;
    text-align: center;
}

@media (max-width: 767px) {

    .property-list-card-inner {
        display: block;
    }

    .property-list-image {
        width: 100%;
        min-width: 100%;
        height: 220px;
    }

    .property-list-locked-image {
        min-height: 220px;
    }

    .property-list-content {
        padding: 18px;
    }

    .property-list-member-details {
        grid-template-columns: 1fr;
    }

    .property-list-view-button {
        width: 100%;
    }
}

</style>